import { useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { isNativePlatform } from '@/lib/device/platform';
import {
  cancelScan,
  finishScan,
  getScannerState,
  subscribeScanner,
  type ScanBackend,
} from '@/lib/device/scanner';
import { startNativeScan } from '@/lib/device/scanner-native';
import { createWebScanBackend } from '@/lib/device/scanner-web';
import { IconCancel } from '@/lib/icons';

/** Makes the page see-through so the native camera preview behind it shows. */
function makeTransparent(): () => void {
  const targets = [document.documentElement, document.body, document.getElementById('root')];
  const saved = targets.map((el) => el?.style.cssText ?? '');
  for (const el of targets) el?.style.setProperty('background', 'transparent');
  // The page behind the camera must not show through the transparent overlay.
  targets[2]?.style.setProperty('visibility', 'hidden');
  return () => {
    targets.forEach((el, index) => {
      if (el) el.style.cssText = saved[index] ?? '';
    });
  };
}

/**
 * The full-screen scanner (fuel-pda.md 4.3 and 8): camera, a frame, the hint
 * «وجّه الكاميرا إلى رمز QR في الوصل» and a cancel button. Mounted once at the
 * root; `scanQr()` opens it.
 */
export function ScannerOverlay() {
  const { open } = useSyncExternalStore(subscribeScanner, getScannerState, getScannerState);
  if (!open) return null;
  return createPortal(<ScannerView />, document.body);
}

function ScannerView() {
  const { t } = useTranslation('device');
  const videoRef = useRef<HTMLVideoElement>(null);
  const native = isNativePlatform();

  useEffect(() => {
    let stop: (() => Promise<void> | void) | undefined;
    let cancelled = false;
    const restore = native ? makeTransparent() : undefined;

    let backend: ScanBackend | undefined;
    if (native) backend = startNativeScan;
    else if (videoRef.current) backend = createWebScanBackend(videoRef.current);

    const run = async (): Promise<void> => {
      const stopper = await backend?.({
        onText: (text) => finishScan({ kind: 'text', text }),
        onError: (reason) => finishScan({ kind: 'error', reason }),
      });
      if (!stopper) return;
      if (cancelled) await stopper();
      else stop = stopper;
    };
    void run();

    return () => {
      cancelled = true;
      restore?.();
      void stop?.();
    };
  }, [native]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('scanner.title')}
      data-testid="scanner-overlay"
      className={`fixed inset-0 z-[100] flex flex-col ${native ? '' : 'bg-black'}`}
    >
      {native ? null : (
        <video
          ref={videoRef}
          muted
          playsInline
          className="absolute inset-0 size-full object-cover"
        />
      )}
      <div className="relative flex flex-1 items-center justify-center p-8">
        <div
          aria-hidden="true"
          className="aspect-square w-full max-w-72 rounded-2xl border-4 border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"
        />
      </div>
      <div className="relative flex flex-col items-center gap-4 bg-black/70 p-4 pb-8 text-white">
        <p className="text-center text-base font-medium" role="status">
          {t('scanner.hint')}
        </p>
        <Button
          type="button"
          variant="secondary"
          className="min-h-12 w-full"
          data-testid="scanner-cancel"
          onClick={cancelScan}
        >
          <IconCancel aria-hidden />
          {t('scanner.cancel')}
        </Button>
      </div>
    </div>
  );
}
